/** @type {Record<string, import("./types.js").LandingPageContent>} */
export const landingContentEn = {
  "zaagplan-maken": {
    "title": "Create a cut plan | Free online | Plaatplan",
    "description": "Create a free cut plan for sheet material. Enter your panels, see the cut layout, and export a work plan with cut list for the hardware store or cutting service.",
    "h1": "Create a cut plan for sheet material",
    "lead": "A cut plan (or cut diagram) shows how to cut parts from standard sheets. With Plaatplan you enter dimensions and quantities, instantly see how many sheets you need, and take a printable work plan with you. Free, no account required.",
    "ctaLabel": "Start planning",
    "sections": [
      {
        "h2": "What is a cut plan?",
        "paragraphs": [
          "A cut plan is an overview of how rectangular parts are laid out on one or more sheets. You see which cuts are needed, how much waste is left over, and which dimensions to give the cutting service.",
          "Plaatplan is built for DIYers planning multiple projects at once, for example, a cabinet and a desk from the same MDF sheet."
        ]
      },
      {
        "h2": "Cut plan, cut list and work plan",
        "paragraphs": [
          "The cut plan is the visual layout: how parts sit on the sheet. The cut list is the table of cut sizes and quantities the cutting counter needs. The work plan is the complete package you print or bring: cut list, instructions and a diagram per sheet."
        ]
      },
      {
        "h2": "Cut plan or cut diagram?",
        "paragraphs": [
          "In practice, cut plan and cut diagram mean the same thing: the layout of parts on a sheet. Hardware stores and cutting services use both terms."
        ]
      }
    ]
  },
  "zaagplan-mdf": {
    "title": "Create an MDF cut plan | Free 2440×1220 layout | Plaatplan",
    "description": "Free cut plan for MDF sheets. Calculate how many 2440×1220 mm sheets you need, with kerf and cut layout. Export a cut list as PDF or CSV for the hardware store.",
    "h1": "Create a cut plan for MDF",
    "lead": "MDF is popular for cabinets, alcoves, and interior projects. Plaatplan helps you lay out panels efficiently on standard MDF sheets (usually 2440 × 1220 mm, 18 mm thick) and calculates how many sheets to order.",
    "ctaLabel": "Start MDF cut plan",
    "sections": [
      {
        "h2": "How many MDF sheets do I need?",
        "paragraphs": [
          "Enter all your parts with length, width, and quantity. Plaatplan groups panels by thickness and distributes them across as few sheets as possible. You instantly see whether everything fits and how many standard sheets to buy.",
          "Tip: add leftover material you already have. The planner uses smaller pieces before opening new sheets."
        ]
      },
      {
        "h2": "Getting MDF cut at the hardware store",
        "paragraphs": [
          "Export your work plan as a PDF or print it. It includes dimensions, quantities, and edge finishing in a vendor-neutral format you can hand in at Hornbach, Gamma, Karwei, or another cutting service."
        ]
      },
      {
        "h2": "Kerf and grain direction for MDF",
        "paragraphs": [
          "With MDF you can usually rotate panels 90° for a more efficient layout. Set the blade width (kerf) to 2.2 mm or your saw blade's value. Plaatplan accounts for this in the layout."
        ]
      }
    ]
  },
  "zaagplan-multiplex": {
    "title": "Create a plywood cut plan | Grain direction & layout | Plaatplan",
    "description": "Create a cut plan for plywood and birch plywood. Lock grain direction, calculate sheets, and export a work plan with cut list for cutting yourself or having it cut.",
    "h1": "Create a cut plan for plywood",
    "lead": "With visible plywood, grain direction matters. Plaatplan lets you lock panels to grain direction, calculates the layout on standard sheets, and shows a visual cut diagram per sheet.",
    "ctaLabel": "Start plywood cut plan",
    "sections": [
      {
        "h2": "Grain direction for plywood",
        "paragraphs": [
          "Lock grain direction when the wood grain is visible in your project. Panels will not rotate unexpectedly. For hidden structural panels, you can allow rotation to reduce waste."
        ]
      },
      {
        "h2": "Plywood cut layout",
        "paragraphs": [
          "Enter your panels per subproject: sides, shelves, back panels. Parts with the same thickness can share one sheet, color-coded per project. That way you see whether you need one or two plywood sheets."
        ]
      },
      {
        "h2": "From cut plan to workshop",
        "paragraphs": [
          "Print the work plan or export a CSV. You get a cut list with dimensions, cut sequence, and optional strip mode for the cutting service."
        ]
      }
    ]
  },
  "zaagplan-spaanplaat": {
    "title": "Create a particle board cut plan | Free online | Plaatplan",
    "description": "Free cut plan for particle board. Lay out panels on standard sizes, count sheets, and export a work plan with cut list for the hardware store or your own saw table.",
    "h1": "Create a cut plan for particle board",
    "lead": "Particle board is an affordable choice for cabinets, back panels, and structural panels. Plaatplan calculates your cut layout on standard sizes and helps limit waste.",
    "ctaLabel": "Start particle board plan",
    "sections": [
      {
        "h2": "Laying out particle board",
        "paragraphs": [
          "Choose your sheet size (e.g. 2440 × 1220 mm) and thickness. Enter parts per project. The planner optimizes the layout and shows offcuts.",
          "For panels with a decorative finish (white, oak, concrete look), you can also use our furniture panel guide."
        ]
      },
      {
        "h2": "Cut diagram for the hardware store",
        "paragraphs": [
          "A clear cut diagram saves time at the cutting counter. With Plaatplan you have dimensions, quantities, and a visual layout per sheet, ready to print."
        ]
      }
    ]
  },
  "zaagplan-meubelpaneel": {
    "title": "Create a furniture panel cut plan | Melamine & decor | Plaatplan",
    "description": "Free cut plan for furniture panel and melamine. Lay out panels with decor finish, count sheets, and take a work plan with cut list to the hardware store or cutting service.",
    "h1": "Create a cut plan for furniture panel",
    "lead": "Furniture panel (often with melamine or HPL top layer) is the standard for cabinets and interior projects. Plaatplan helps you lay out panels on standard sheets, accounts for kerf, and delivers a printable work plan. Free, no account required.",
    "ctaLabel": "Start furniture panel plan",
    "sections": [
      {
        "h2": "Furniture panel, melamine, and decor",
        "paragraphs": [
          "At the hardware store it is called furniture panel, melamine board, or panel with a decorative finish: white, oak, concrete look, or another surface. For layout, size and thickness matter most; the planner works with your dimensions and quantities.",
          "Does your decor have a direction (grain or pattern)? Lock grain direction so panels do not rotate unexpectedly."
        ]
      },
      {
        "h2": "How many sheets do I need?",
        "paragraphs": [
          "Enter sides, shelves, doors, and back panels per subproject. Panels with the same thickness can share one sheet, color-coded per project. That way you instantly see how many standard sheets to order."
        ]
      },
      {
        "h2": "Take it to the cutting service",
        "paragraphs": [
          "Export your work plan as a PDF or print it. It includes dimensions, quantities, and edge finishing in a vendor-neutral format, usable at Hornbach, Gamma, Karwei, and other cutting services."
        ]
      }
    ]
  },
  "bouwmarkt-zaagplan": {
    "title": "Get sheets cut at the hardware store | Bring your cut plan | Plaatplan",
    "description": "Create a free cut plan for the hardware store or cutting service. Getting sheets cut at Hornbach, Gamma, or Karwei? Bring a work plan with cut list, dimensions and layout.",
    "h1": "Get sheets cut at the hardware store",
    "lead": "At Hornbach, Gamma, Karwei, and other hardware stores you can get sheets cut, as long as you provide clear dimensions and quantities. Plaatplan creates that cut plan for you: layout on the sheet, number of sheets, and a printable work plan.",
    "ctaLabel": "Create cut plan",
    "sections": [
      {
        "h2": "What do you bring to the cutting counter?",
        "paragraphs": [
          "A good cut plan lists length, width, and quantity per part, plus edge finishing if needed. Plaatplan exports a vendor-neutral work plan with cut list: not a brand-specific form, but everything the cutting staff needs.",
          "Print the work plan or open it on your phone. Check beforehand that your blade width (kerf) matches the hardware store's."
        ]
      },
      {
        "h2": "Hornbach, Gamma, Karwei: does it work everywhere?",
        "paragraphs": [
          "Plaatplan is not tied to one hardware store. The export is generic: dimensions, quantities, cut layout, and optionally a visual diagram per sheet. You can use the same plan at different cutting services.",
          "Note: every cutting service has its own rules for minimum dimensions, edge banding, and maximum cuts per sheet. Always check locally."
        ]
      },
      {
        "h2": "From layout to purchase",
        "paragraphs": [
          "Plaatplan counts how many standard sheets you need and shows which offcuts remain. That way you do not over-order material and know exactly what to hand in at the cutting counter."
        ]
      }
    ]
  },
  "zaagindeling": {
    "title": "Calculate cut layout for sheets | Plaatplan",
    "description": "Calculate the cut layout for sheet material for free. See how panels sit on the sheet, with kerf, leftover material, and export to work plan with cut list.",
    "h1": "Calculate cut layout for sheet material",
    "lead": "Cut layout is dividing parts across one or more sheets. Plaatplan does this visually: you see every panel on the sheet, which sheets are new, and which offcuts are used.",
    "ctaLabel": "Calculate cut layout",
    "sections": [
      {
        "h2": "From cut list to layout",
        "paragraphs": [
          "Start with a list of panels (length × width × quantity). Plaatplan turns it into a layout on standard sheets, no manual puzzling on paper."
        ]
      },
      {
        "h2": "Multiple thicknesses and projects",
        "paragraphs": [
          "Panels with different thicknesses go on separate sheets. Multiple projects in one session? Each subproject gets a color in the cut diagram."
        ]
      }
    ]
  }
};
